import React, { useEffect, useMemo } from 'react';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { Code, Icon, Text, Callout, Select, Box, TextField } from '@scale/scaleui-radix';
import { faCheckCircle, faCircleExclamation, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useCompassCard } from '@scale/compass-ui/store';
import type { SphereEngineExecutionContext } from '@scale/genai-compass-shared/cards/sphere_engine_execution';
import { CardType } from '@scale/genai-compass-shared/cardTypes';
import { Column } from '@scale/compass-shared/types';
import FileColumnSelection from './FileColumnSelection';

interface SphereProjectIdColumnCheckProps {
  columns: Column[];
}

// Helper function to detect sphere project ID column
const findSphereProjectIdColumn = (columns: Column[]) => {
  return Array.isArray(columns)
    ? columns.find(
        c =>
          c &&
          c.header &&
          (c.header.toLowerCase() === 'sphere_project_id' ||
            c.header.toLowerCase() === 'sphereprojectid'),
      )
    : null;
};

const SphereProjectIdColumnCheck = ({ columns }: SphereProjectIdColumnCheckProps) => {
  const sphereProjectIdColumn = findSphereProjectIdColumn(columns);
  const hasSphereProjectIdColumn = !!sphereProjectIdColumn;

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`flex items-center gap-2 ${
          hasSphereProjectIdColumn ? 'text-neutral-600' : 'text-red-600'
        }`}
      >
        <Icon
          icon={hasSphereProjectIdColumn ? faCheckCircle : faCircleExclamation}
          size="sm"
          label="Info"
          className={hasSphereProjectIdColumn ? 'text-green-600' : 'text-red-600'}
        />
        <Text as="p" size="2">
          {hasSphereProjectIdColumn ? (
            <>
              Found required column <Code>{sphereProjectIdColumn?.header}</Code> for sphere
              execution
            </>
          ) : (
            <>
              Missing required column: expected <Code>sphere_project_id</Code> or{' '}
              <Code>sphereProjectId</Code>
            </>
          )}
        </Text>
      </div>
    </div>
  );
};

// eslint-disable-next-line complexity
const SphereEngineExecutionCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const { retrievedOutput } = useCompassCard(index - 1);
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);
  const columns = useMemo(
    () => retrievedOutput?.response?.schema || [],
    [retrievedOutput?.response?.schema],
  );
  const context = (card?.defaultContext as SphereEngineExecutionContext) || {
    cardType: CardType.SPHERE_ENGINE_EXECUTION,
    fileColumnIds: [],
    sphere_scenario: 'run',
  };

  const hasSphereProjectIdColumn = !!findSphereProjectIdColumn(columns);

  // Generic context update helper
  const updateContext = (updates: Partial<SphereEngineExecutionContext>) => {
    const updatedContext: SphereEngineExecutionContext = {
      ...context,
      ...updates,
    };
    updateCardContext(updatedContext);
  };

  // Handle file column selection
  const setSelectedFileColumnsByIds = (ids: string[]) => {
    updateContext({ fileColumnIds: ids });
  };

  // Handle regex input change
  const setFileColumnRegex = (regex: string) => {
    updateContext({ fileColumnRegex: regex });
  };

  // Handle scenario input change
  const setScenario = (newScenario: string) => {
    updateContext({ sphere_scenario: newScenario });
  };

  // Handle timeout input change
  const setTimeoutSeconds = (timeout: string) => {
    const timeoutNum = parseInt(timeout, 10);
    if (!isNaN(timeoutNum) && timeoutNum > 0) {
      updateContext({ timeoutSeconds: timeoutNum });
    }
  };

  // Create options for scenario selection
  const scenarioOptions = [
    { id: 'init', name: 'init' },
    { id: 'build', name: 'build' },
    { id: 'run', name: 'run' },
    { id: 'test', name: 'test' },
    { id: 'post', name: 'post' },
  ];
  const { matchedColumns, regexError } = useMemo(() => {
    const regex = context.fileColumnRegex;
    if (!regex) {
      return { matchedColumns: [], regexError: '' };
    }

    try {
      const regexPattern = new RegExp(regex);
      const matches = Array.isArray(columns)
        ? columns.filter(col => col && col.header && regexPattern.test(col.header))
        : [];
      return { matchedColumns: matches, regexError: '' };
    } catch (error) {
      return {
        matchedColumns: [],
        regexError: error instanceof Error ? error.message : 'Invalid regex pattern',
      };
    }
  }, [context.fileColumnRegex, columns]);

  // Update card can submit status
  useEffect(() => {
    const canSubmit = hasSphereProjectIdColumn;
    updateCardCanSubmit(canSubmit);
  }, [hasSphereProjectIdColumn]);

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Instructions */}
      <Callout.Root color={hasSphereProjectIdColumn ? 'blue' : 'red'}>
        <Callout.Text>
          <Text as="p" className="text-sm">
            {hasSphereProjectIdColumn ? (
              <>
                This card executes code in isolated Sphere Engine containers, with each row in your
                data becoming a separate execution environment. The <Code>sphere_project_id</Code>{' '}
                column is essential as it tells the system which specific Sphere Engine project to
                use for each execution - this determines the runtime environment, installed
                packages, and execution settings for your code.
                <br />
                <br />
                You can inject files from your data columns into each container&apos;s workspace
                directory. Select columns manually or use regex patterns like <Code>
                  ^code_.*
                </Code>{' '}
                to automatically match columns containing file content. These files will be
                available at <Code>/workspace/[column_name]</Code> inside the container, so your
                code can read and process them during execution.
                <br />
                <br />
                The card runs your chosen scenario (init, build, run, test, or post) and captures
                all the output in a new <Code>sphere_out</Code> column. This includes everything
                your code prints to stdout, any error messages or warnings from stderr, and
                execution status information. All executions run in parallel for speed, handling up
                to 50 concurrent submissions at once.
              </>
            ) : (
              <>
                To use this card, your data must contain a <Code>sphere_project_id</Code> (or{' '}
                <Code>sphereProjectId</Code>) column. This column is used to identify the Sphere
                Engine project for submission execution.
              </>
            )}
          </Text>
        </Callout.Text>
      </Callout.Root>

      {/* Precheck Validation */}
      <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
        <div className="flex items-center gap-2 mb-3">
          <Icon icon={faInfoCircle} size="sm" label="Info" className="text-blue-600" />
          <Text as="p" size="2" className="font-medium">
            Configuration Check
          </Text>
        </div>
        <SphereProjectIdColumnCheck columns={columns} />
      </div>

      {/* File Column Selection */}
      {hasSphereProjectIdColumn && (
        <FileColumnSelection
          context={context}
          columns={columns}
          matchedColumns={matchedColumns}
          regexError={regexError}
          onFileColumnRegexChange={setFileColumnRegex}
          onSelectedFileColumnsByIds={setSelectedFileColumnsByIds}
        />
      )}

      {/* Scenario Configuration */}
      {hasSphereProjectIdColumn && (
        <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
          <div className="mb-3">
            <Text as="p" size="2" className="font-medium">
              Sphere Scenario
            </Text>
          </div>
          <div className="mb-3">
            <Text as="p" size="2" className="text-gray-700 mb-2">
              Enter the{' '}
              <a
                href="https://docs.sphere-engine.com/containers/handbook/execution-stages"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                scenario name
              </a>{' '}
              to use for Sphere Engine submission execution. These correspond to the button names in
              the Sphere IDE.
            </Text>
          </div>
          <Box maxWidth="300px">
            <Select.Root
              value={context.sphere_scenario || 'run'}
              onValueChange={(value: string | null) => setScenario(value || 'run')}
            >
              <Select.Trigger placeholder="Select scenario..." />
              <Select.Content>
                {scenarioOptions.map(option => (
                  <Select.Item key={option.id} value={option.id}>
                    {option.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
        </div>
      )}

      {/* Timeout Configuration */}
      {hasSphereProjectIdColumn && (
        <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
          <div className="mb-3">
            <Text as="p" size="2" className="font-medium">
              Execution Timeout
            </Text>
          </div>
          <div className="mb-3">
            <Text as="p" size="2" className="text-gray-700 mb-2">
              Maximum time in seconds to wait for each submission to complete. Default is 250
              seconds.
            </Text>
          </div>
          <Box maxWidth="200px">
            <TextField.Root
              type="number"
              placeholder="250"
              value={context.timeoutSeconds?.toString() || '250'}
              onChange={e => setTimeoutSeconds(e.target.value)}
              size="2"
              min="1"
              max="600"
            />
          </Box>
        </div>
      )}
    </div>
  );
};

export default SphereEngineExecutionCardComponent;
